import { keepPreviousData } from "@tanstack/react-query"
import {
	getMarketingContextGetMarketingContextQueryKey,
	getMarketingContextGetMarketingContextQueryOptions,
	useMarketingContextGetMarketingContext,
} from "@/shared/api/generated/marketing-context/marketing-context"
import { GetMarketingContextResponse } from "@/shared/api/generated/marketing-context/marketing-context.zod"
import type { MarketingContextResponse } from "@/shared/api/generated/types/marketing"
import { validateSuccessResponse } from "@/shared/lib/zod-validation-helpers"
import type { QueryHookOptions } from "@/shared/typings"

import { MARKETING_CONTEXT_STALE } from "../consts"

function buildMarketingContextQueryOptions(options?: QueryHookOptions) {
	return {
		enabled: options?.enabled ?? true,
		placeholderData:
			(options?.keepPreviousData ?? true) ? keepPreviousData : undefined,
		staleTime: MARKETING_CONTEXT_STALE,
		select(data: unknown) {
			return validateSuccessResponse(GetMarketingContextResponse, data)
		},
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		refetchOnMount: false,
	} as const
}

export function getMarketingContextQueryOptions(options?: QueryHookOptions) {
	return getMarketingContextGetMarketingContextQueryOptions({
		query: buildMarketingContextQueryOptions(options),
	})
}

export function useMarketingContext(options?: QueryHookOptions) {
	return useMarketingContextGetMarketingContext({
		query: buildMarketingContextQueryOptions(options),
	})
}

export function getMarketingContextQueryKey() {
	return getMarketingContextGetMarketingContextQueryKey()
}

export function isMarketingSectionVisible(
	context: MarketingContextResponse | undefined | null,
) {
	return Boolean(context?.marketingEnabled)
}

export function isMarketingTermsAccepted(
	context: MarketingContextResponse | undefined | null,
) {
	return Boolean(context?.marketingEnabled && context?.marketingAccepted)
}

export type MarketingNavVisibility = "loading" | "hidden" | "visible"

export function getMarketingNavVisibility(
	context: MarketingContextResponse | null | undefined,
	query: {
		isLoading: boolean
		isError: boolean
		isFetched: boolean
	},
): MarketingNavVisibility {
	if (!query.isFetched || query.isLoading) {
		return "loading"
	}

	if (query.isError || !context) {
		return "hidden"
	}

	return isMarketingSectionVisible(context) ? "visible" : "hidden"
}
